import { useEffect, useMemo, useState } from 'react'
import type { AgentOutputProps } from '../types'
import { apiClient } from '@/services/api'
import ZiroGraphDiagram from '@/components/intelligent/ZiroGraphDiagram'

/**
 * OrchestrationDAGRenderer — reuses the Playground's ZiroGraphDiagram to show
 * an auto-orchestrator execution's full task DAG.
 *
 * Data source priority:
 *   1. props.result.task_dag / agent_data.task_dag (when the renderer is
 *      handed a full result payload)
 *   2. GET /api/v1/executions/{execution.id} — falls back to fetching the
 *      persisted task_dag_json so this also works from the Executions page
 *      where only an execution id is available.
 */

interface RawTask {
  id: string
  agent_name: string
  depends_on?: string[]
  status?: string
  description?: string
  duration_ms?: number
}

interface RawTaskDag {
  total_tasks?: number
  by_status?: Record<string, number>
  tasks: RawTask[]
}

interface GraphData {
  name: string
  nodes: any[]
  edges: any[]
  node_groups: Record<string, string[]>
  layout: { direction: string; layers: string[][] }
}

function pickTaskDag(result: any): RawTaskDag | null {
  if (!result) return null
  const candidates = [
    result.task_dag,
    result.task_dag_json,
    result.agent_data?.task_dag,
    result.agent_data?.task_dag_json,
  ]
  for (const c of candidates) {
    if (c && Array.isArray(c.tasks)) return c as RawTaskDag
  }
  return null
}

function buildLayers(tasks: RawTask[]): string[][] {
  // Topological layering: each task goes one layer deeper than its deepest dep.
  const depth: Record<string, number> = {}
  const taskById = Object.fromEntries(tasks.map((t) => [t.id, t]))
  function depthOf(id: string, stack: Set<string>): number {
    if (depth[id] !== undefined) return depth[id]
    if (stack.has(id)) return 0 // cycle guard
    const t = taskById[id]
    if (!t || !t.depends_on || t.depends_on.length === 0) {
      depth[id] = 0
      return 0
    }
    stack.add(id)
    const d = 1 + Math.max(...t.depends_on.map((d) => depthOf(d, stack)))
    stack.delete(id)
    depth[id] = d
    return d
  }
  for (const t of tasks) depthOf(t.id, new Set())

  const byLayer: Record<number, string[]> = {}
  for (const t of tasks) {
    const d = depth[t.id] ?? 0
    if (!byLayer[d]) byLayer[d] = []
    byLayer[d].push(t.id)
  }
  const keys = Object.keys(byLayer).map(Number).sort((a, b) => a - b)
  return keys.map((k) => byLayer[k])
}

function toGraphData(dag: RawTaskDag, rootLabel: string): GraphData {
  const rootId = '__orchestrator'
  const rootNode = {
    id: rootId,
    name: rootLabel,
    type: 'supervisor',
    description: '',
    position: { x: 0, y: 0 },
    icon: 'sparkles',
    status: 'completed',
  }
  const taskNodes = dag.tasks.map((t) => ({
    id: t.id,
    name: t.agent_name || t.id,
    type: 'specialist',
    description: t.description || (t.id !== t.agent_name ? t.id : ''),
    position: { x: 0, y: 0 },
    icon: 'cpu',
    status:
      t.status === 'completed' ? 'completed'
      : t.status === 'failed' || t.status === 'cancelled' ? 'failed'
      : t.status === 'running' ? 'running'
      : 'idle',
  }))

  const edges: any[] = []
  const idsWithDeps = new Set<string>()
  for (const t of dag.tasks) {
    if (t.depends_on && t.depends_on.length > 0) {
      idsWithDeps.add(t.id)
      for (const dep of t.depends_on) {
        edges.push({ source: dep, target: t.id, label: '', condition: null, is_conditional: false })
      }
    }
  }
  // Tasks with no deps connect to the orchestrator root.
  for (const t of dag.tasks) {
    if (!idsWithDeps.has(t.id)) {
      edges.push({ source: rootId, target: t.id, label: '', condition: null, is_conditional: false })
    }
  }

  const layers = [[rootId], ...buildLayers(dag.tasks)]

  return {
    name: `${rootLabel} · ${dag.tasks.length} tasks`,
    nodes: [rootNode, ...taskNodes],
    edges,
    node_groups: {
      supervisors: [rootId],
      specialists: taskNodes.map((n) => n.id),
    },
    layout: { direction: 'TB', layers },
  }
}

function buildStatusMap(dag: RawTaskDag): Record<string, 'pending' | 'active' | 'completed' | 'error'> {
  const map: Record<string, 'pending' | 'active' | 'completed' | 'error'> = {
    __orchestrator: 'completed',
  }
  for (const t of dag.tasks) {
    if (t.status === 'completed') map[t.id] = 'completed'
    else if (t.status === 'failed' || t.status === 'cancelled') map[t.id] = 'error'
    else if (t.status === 'running') map[t.id] = 'active'
    else map[t.id] = 'pending'
  }
  return map
}

export default function OrchestrationDAGRenderer(props: AgentOutputProps) {
  // First try inline result
  const fromResult = useMemo(() => pickTaskDag(props.result), [props.result])

  const [fetched, setFetched] = useState<RawTaskDag | null>(null)
  const [fetchErr, setFetchErr] = useState<string | null>(null)
  // The persisted DAG is keyed on request_id (string), not the UUID execution
  // row id. Prefer request_id when available; fall back to id for the rare
  // surfaces that only have that.
  const lookupKey = props.execution?.request_id || props.execution?.id

  useEffect(() => {
    if (fromResult || !lookupKey) return
    let cancelled = false
    ;(apiClient as any).client
      .get(`/api/v1/executions/history/${lookupKey}`)
      .then((resp: any) => {
        if (cancelled) return
        const td = resp?.data?.task_dag_json
        if (td && Array.isArray(td.tasks)) setFetched(td)
      })
      .catch((e: any) => {
        if (!cancelled) setFetchErr(e?.message || 'failed to load execution')
      })
    return () => {
      cancelled = true
    }
  }, [fromResult, lookupKey])

  const dag = fromResult || fetched || (props.execution?.task_dag_json as RawTaskDag | undefined) || null
  const rootLabel = props.agent.display_name || props.agent.name || 'Orchestrator'
  const graphData = useMemo(
    () => (dag ? toGraphData(dag, rootLabel) : null),
    [dag, rootLabel],
  )
  const statusMap = useMemo(() => (dag ? buildStatusMap(dag) : {}), [dag])

  if (!dag || !graphData) {
    return (
      <div className="text-sm text-muted-foreground italic">
        {fetchErr ? `Couldn't load orchestration DAG: ${fetchErr}` : 'No orchestration DAG available.'}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">
        {dag.tasks.length} tasks
        {dag.by_status && (
          <>
            {' · '}
            {Object.entries(dag.by_status).map(([k, v]) => `${v} ${k}`).join(' · ')}
          </>
        )}
      </div>
      <div className="border border-border rounded-lg overflow-hidden" style={{ height: 480 }}>
        <ZiroGraphDiagram
          customGraphData={graphData}
          activeNodes={statusMap}
          currentPhase="completed"
          showFullGraph
        />
      </div>
    </div>
  )
}
